// client/src/views/Interactive_parts/OverlayEditing.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { resolveUploadsUrl } from './InteractiveMapInternals.js';

export function assetToBounds(a, imageSize, fallbackSize = 256) {
  if (!a || !Array.isArray(imageSize)) return null;

  const ax = Number.isFinite(a.anchor_x) ? a.anchor_x : 0.5;
  const ay = Number.isFinite(a.anchor_y) ? a.anchor_y : 0.5;

  const w = Number(a.placement_width) || fallbackSize;
  const h = Number(a.placement_height) || fallbackSize;

  const mapW = Array.isArray(imageSize) ? Number(imageSize[0]) || 0 : 0;
  const mapH = Array.isArray(imageSize) ? Number(imageSize[1]) || 0 : 0;

  const cx = Number.isFinite(a.placement_x) ? a.placement_x : mapW / 2;
  const cy = Number.isFinite(a.placement_y) ? a.placement_y : mapH / 2;

  const x0 = cx - w * ax;
  const y0 = cy - h * ay;
  const x1 = x0 + w;
  const y1 = y0 + h;

  return [
    [y0, x0],
    [y1, x1],
  ];
}

function getOverlayImageUrl(asset) {
  if (!asset) return null;
  const raw =
    asset.url ||
    asset.image_url ||
    asset.path ||
    asset.file_path ||
    asset.filepath;
  return raw ? resolveUploadsUrl(raw) : null;
}

export function useOverlayEditing({ mapData, imageSize }) {
  const [selectedOverlayAsset, setSelectedOverlayAsset] = useState(null);
  const [overlayBounds, setOverlayBounds] = useState(null);
  const [overlaySaving, setOverlaySaving] = useState(false);
  const [overlayError, setOverlayError] = useState(null);
  const [overlayEditMode, setOverlayEditMode] = useState(false);
  const [overlayEditLocked, setOverlayEditLocked] = useState(false);
  const overlayBoundsSnapshotRef = useRef(null);
  const [overlayAssets, setOverlayAssets] = useState([]);

  const mapId = mapData?.id || mapData?.map_id || null;

  const loadOverlays = useCallback(
    async (options = {}) => {
      const { autoSelectIfNone = false } = options;
      if (!mapId) return;

      try {
        const res = await fetch(`/api/tabletop/maps/${mapId}/assets`, {
          credentials: 'include',
        });
        const data = await res.json().catch(() => null);
        if (!Array.isArray(data)) {
          setOverlayAssets([]);
          return;
        }

        const allAssetsWithUrl = data.filter((a) => {
          if (!a) return false;
          const url =
            a.url ||
            a.image_url ||
            a.file_path ||
            a.path ||
            a.filepath;
          return !!url;
        });

        setOverlayAssets(allAssetsWithUrl);

        if (
          autoSelectIfNone &&
          !selectedOverlayAsset &&
          !overlayBounds &&
          allAssetsWithUrl.length
        ) {
          const primary =
            allAssetsWithUrl.find((a) => a.visible !== 0) ||
            allAssetsWithUrl[0];

          setSelectedOverlayAsset(primary);
        }
      } catch {
        setOverlayAssets([]);
      }
    },
    [mapId]
  );

  useEffect(() => {
    if (!mapId) return;
    loadOverlays({ autoSelectIfNone: true });
  }, [mapId, loadOverlays]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.mapId && String(detail.mapId) !== String(mapId)) return;
      if (!mapId) return;
      loadOverlays({ autoSelectIfNone: false });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('tabletop-assets-updated', handler);
      return () =>
        window.removeEventListener('tabletop-assets-updated', handler);
    }
    return undefined;
  }, [mapId, loadOverlays]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      const assetId = detail.assetId;

      if (assetId == null || assetId === 'none') {
        setSelectedOverlayAsset(null);
        return;
      }

      if (!Array.isArray(overlayAssets) || !overlayAssets.length) {
        return;
      }

      const next = overlayAssets.find((a) => String(a.id) === String(assetId));
      if (!next) return;

      setSelectedOverlayAsset(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('tabletop-overlay-select-asset', handler);
      return () =>
        window.removeEventListener('tabletop-overlay-select-asset', handler);
    }
    return undefined;
  }, [overlayAssets]);

  useEffect(() => {
    if (!selectedOverlayAsset) {
      setOverlayBounds(null);
      return;
    }
    setOverlayBounds(assetToBounds(selectedOverlayAsset, imageSize));
  }, [selectedOverlayAsset, imageSize]);

  const saveOverlayPlacement = useCallback(async () => {
    if (!selectedOverlayAsset || !overlayBounds) return;
    setOverlaySaving(true);
    setOverlayError(null);
    try {
      const [[y0, x0], [y1, x1]] = overlayBounds;
      const width = Math.max(0.00001, x1 - x0);
      const height = Math.max(0.00001, y1 - y0);
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;

      const patch = {
        placement_x: cx,
        placement_y: cy,
        placement_width: width,
        placement_height: height,
      };

      const res = await fetch(
        `/api/tabletop/assets/${selectedOverlayAsset.id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        }
      );
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          (body && (body.error || body.message)) ||
          `${res.status} ${res.statusText}`;
        throw new Error(msg);
      }

      setSelectedOverlayAsset(body);
      setOverlayEditMode(false);
      overlayBoundsSnapshotRef.current = null;

      try {
        const mapIdFromAsset =
          body?.map_id ??
          selectedOverlayAsset.map_id ??
          mapData?.id ??
          mapData?.map_id ??
          null;
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('tabletop-assets-updated', {
              detail: mapIdFromAsset ? { mapId: mapIdFromAsset } : {},
            })
          );
        }
      } catch {}
    } catch (err) {
      setOverlayError(err.message || 'Failed to save overlay');
    } finally {
      setOverlaySaving(false);
    }
  }, [selectedOverlayAsset, overlayBounds, mapData]);

  const handleStartOverlayEdit = useCallback(
    (asset, locked) => {
      if (!asset) return;

      setSelectedOverlayAsset(asset);
      setOverlayEditLocked(!!locked);

      const pw = Number(asset.placement_width);
      const ph = Number(asset.placement_height);
      const hasStoredSize =
        Number.isFinite(pw) && Number.isFinite(ph) && pw > 0 && ph > 0;

      let useStoredPlacement = false;
      if (hasStoredSize) {
        const isSquare = Math.abs(pw - ph) < 1e-3;
        const isVerySmall = pw <= 260 && ph <= 260;
        useStoredPlacement = !(isSquare && isVerySmall);
      }

      if (useStoredPlacement) {
        const b = assetToBounds(asset, imageSize);
        setOverlayBounds(b);
        overlayBoundsSnapshotRef.current = b
          ? JSON.parse(JSON.stringify(b))
          : null;
        setOverlayEditMode(true);
        return;
      }

      const url = getOverlayImageUrl(asset);
      if (!url) {
        const b = assetToBounds(asset, imageSize);
        setOverlayBounds(b);
        overlayBoundsSnapshotRef.current = b
          ? JSON.parse(JSON.stringify(b))
          : null;
        setOverlayEditMode(true);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const naturalW = img.naturalWidth || img.width || 256;
        const naturalH = img.naturalHeight || img.height || 256;

        const mapW = Array.isArray(imageSize) ? Number(imageSize[0]) || 0 : 0;
        const mapH = Array.isArray(imageSize) ? Number(imageSize[1]) || 0 : 0;

        const clampedW = mapW > 0 ? Math.min(naturalW, mapW) : naturalW;
        const clampedH = mapH > 0 ? Math.min(naturalH, mapH) : naturalH;

        const sizedAsset = {
          ...asset,
          placement_width: clampedW,
          placement_height: clampedH,
        };

        const b = assetToBounds(sizedAsset, imageSize);
        setOverlayBounds(b);
        overlayBoundsSnapshotRef.current = b
          ? JSON.parse(JSON.stringify(b))
          : null;
        setOverlayEditMode(true);
      };
      img.onerror = () => {
        const b = assetToBounds(asset, imageSize);
        setOverlayBounds(b);
        overlayBoundsSnapshotRef.current = b
          ? JSON.parse(JSON.stringify(b))
          : null;
        setOverlayEditMode(true);
      };
      img.src = url;
    },
    [imageSize]
  );

  const handleCancelOverlayEdit = useCallback(() => {
    if (overlayBoundsSnapshotRef.current) {
      setOverlayBounds(
        JSON.parse(JSON.stringify(overlayBoundsSnapshotRef.current))
      );
    }
    setOverlayEditMode(false);
    setOverlayError(null);
  }, []);

  const overlayImageUrl = selectedOverlayAsset
    ? getOverlayImageUrl(selectedOverlayAsset)
    : null;

  return {
    selectedOverlayAsset,
    setSelectedOverlayAsset,
    overlayBounds,
    setOverlayBounds,
    overlaySaving,
    overlayError,
    overlayEditMode,
    overlayEditLocked,
    overlayImageUrl,
    overlayAssets,
    handleStartOverlayEdit,
    handleCancelOverlayEdit,
    saveOverlayPlacement,
  };
}

export function OverlayEditHUD({
  bounds,
  setBounds,
  locked,
  editing,
  overlays,
  selectedOverlay,
  onSelectOverlay,
  onUiActiveChange,
  menuTitle,
  menuSize,
  baseLabel,
}) {
  const map = useMap();

  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null);
  const [cursor, setCursor] = useState('default');
  const [opacity, setOpacity] = useState(1);
  const [initialisedFromStorage, setInitialisedFromStorage] = useState(false);

  const overlayChoices = Array.isArray(overlays) ? overlays.slice() : [];
  const selectedOverlayId = selectedOverlay ? String(selectedOverlay.id) : 'none';

  const startRef = useRef({
    containerPt: null,
    bounds: null,
  });

  const HANDLE_SIZE = 10;
  const MIN_SIZE_PX = 10;

  const resolvedMenuTitle = (menuTitle || '').trim() || 'Floors';
  const resolvedMenuSize = (menuSize || 'medium').toLowerCase();
  const hideMenu = resolvedMenuSize === 'hidden';

  const resolvedBaseLabel =
    (baseLabel || '').trim() || 'Ground floor (no overlay)';

  const sizePreset = (() => {
    if (resolvedMenuSize === 'small') {
      return {
        maxWidth: 210,
        padding: '6px 8px',
        fontSize: 11,
        selectPadding: '3px 6px',
      };
    }
    if (resolvedMenuSize === 'large') {
      return {
        maxWidth: 320,
        padding: '10px 12px',
        fontSize: 13,
        selectPadding: '6px 8px',
      };
    }
    return {
      maxWidth: 260,
      padding: '8px 10px',
      fontSize: 12,
      selectPadding: '4px 6px',
    };
  })();

  const getFloorStorageKey = () => {
    if (typeof window === 'undefined') return null;

    let mapKey = 'global';
    if (Array.isArray(overlays) && overlays.length > 0) {
      const first = overlays[0] || {};
      const idGuess = first.map_id ?? first.mapId ?? first.map ?? null;
      if (idGuess != null) {
        mapKey = String(idGuess);
      }
    }
    return `pa_last_floor_${mapKey}`;
  };

  useEffect(() => {
    return () => {
      if (typeof onUiActiveChange === 'function') {
        onUiActiveChange(false);
      }
    };
  }, [onUiActiveChange]);

  useEffect(() => {
    if (!editing) return undefined;

    map.dragging && map.dragging.disable();
    map.doubleClickZoom && map.doubleClickZoom.disable();
    map.boxZoom && map.boxZoom.disable();
    map.keyboard && map.keyboard.disable();

    if (map.tap && typeof map.tap.enabled === 'function' && map.tap.enabled()) {
      map.tap.disable();
    }

    return () => {
      map.dragging && map.dragging.enable();
      map.doubleClickZoom && map.doubleClickZoom.enable();
      map.boxZoom && map.boxZoom.enable();
      map.keyboard && map.keyboard.enable();

      if (
        map.tap &&
        typeof map.tap.enabled === 'function' &&
        !map.tap.enabled()
      ) {
        map.tap.enable();
      }
    };
  }, [map, editing]);

  useEffect(() => {
    if (!selectedOverlay) return;
    const targetUrl = getOverlayImageUrl(selectedOverlay);
    if (!targetUrl) return;

    let initial = null;
    map.eachLayer((layer) => {
      if (
        layer instanceof L.ImageOverlay &&
        typeof layer.options?.opacity === 'number'
      ) {
        const layerUrl =
          layer._url ||
          (layer.options && (layer.options.url || layer.options.href));
        if (!layerUrl) return;

        const lu = String(layerUrl);
        const tu = String(targetUrl);
        if (lu === tu || lu.endsWith(tu) || tu.endsWith(lu)) {
          initial = layer.options.opacity;
        }
      }
    });

    if (initial != null) {
      setOpacity(initial);
    } else {
      setOpacity(1);
    }
  }, [map, selectedOverlay]);

  useEffect(() => {
    if (initialisedFromStorage) return;
    if (!Array.isArray(overlays) || overlays.length === 0) return;

    if (typeof window === 'undefined') {
      setInitialisedFromStorage(true);
      return;
    }

    try {
      const key = getFloorStorageKey();
      if (!key) {
        setInitialisedFromStorage(true);
        return;
      }
      const stored = window.localStorage.getItem(key);
      if (stored == null) {
        setInitialisedFromStorage(true);
        return;
      }

      if (stored === 'none') {
        if (selectedOverlay && typeof onSelectOverlay === 'function') {
          onSelectOverlay(null);
        }
        setInitialisedFromStorage(true);
        return;
      }

      const asset = overlays.find((a) => String(a.id) === String(stored));
      if (
        asset &&
        (!selectedOverlay || String(selectedOverlay.id) !== String(asset.id)) &&
        typeof onSelectOverlay === 'function'
      ) {
        onSelectOverlay(asset);
      }
    } catch {
    } finally {
      setInitialisedFromStorage(true);
    }
  }, [overlays, selectedOverlay, onSelectOverlay, initialisedFromStorage]);

  const applyOpacityToOverlays = (value) => {
    if (!selectedOverlay) return;
    const targetUrl = getOverlayImageUrl(selectedOverlay);
    if (!targetUrl) return;

    map.eachLayer((layer) => {
      if (!(layer instanceof L.ImageOverlay)) return;

      const layerUrl =
        layer._url ||
        (layer.options && (layer.options.url || layer.options.href));
      if (!layerUrl) return;

      const lu = String(layerUrl);
      const tu = String(targetUrl);

      if (!(lu === tu || lu.endsWith(tu) || tu.endsWith(lu))) {
        return;
      }

      if (typeof layer.setOpacity === 'function') {
        layer.setOpacity(value);
      } else if (layer.options) {
        layer.options.opacity = value;
      }
    });
  };

  const handleOpacityChange = (e) => {
    const val = Number(e.target.value);
    setOpacity(val);
    applyOpacityToOverlays(val);
  };

  const handleOverlayChange = (e) => {
    const val = e.target.value || 'none';

    if (typeof window !== 'undefined') {
      try {
        const key = getFloorStorageKey();
        if (key) {
          window.localStorage.setItem(key, val);
        }
      } catch {}
    }

    if (val === 'none') {
      if (typeof onSelectOverlay === 'function') {
        onSelectOverlay(null);
      }
      return;
    }

    const asset = overlayChoices.find((a) => String(a.id) === String(val));
    if (!asset) {
      if (typeof onSelectOverlay === 'function') {
        onSelectOverlay(null);
      }
      return;
    }

    if (typeof onSelectOverlay === 'function') {
      onSelectOverlay(asset);
    }
  };

  const toContainerPoint = (latlng) => map.latLngToContainerPoint(latlng);

  const boundsToContainerBox = (b) => {
    if (!b) return null;
    const [[y0, x0], [y1, x1]] = b;
    const tl = toContainerPoint(L.latLng(y0, x0));
    const br = toContainerPoint(L.latLng(y1, x1));
    return {
      left: Math.min(tl.x, br.x),
      top: Math.min(tl.y, br.y),
      right: Math.max(tl.x, br.x),
      bottom: Math.max(tl.y, br.y),
      width: Math.abs(br.x - tl.x),
      height: Math.abs(br.y - tl.y),
    };
  };

  const hitTest = (pt, box) => {
    if (!box) return { inside: false, handle: null };
    const { left, top, right, bottom } = box;
    const cx = pt.x;
    const cy = pt.y;

    const within = (x, y, hx, hy) =>
      Math.abs(x - hx) <= HANDLE_SIZE && Math.abs(y - hy) <= HANDLE_SIZE;

    const midX = (left + right) / 2;
    const midY = (top + bottom) / 2;

    if (within(cx, cy, left, top)) return { inside: false, handle: 'nw' };
    if (within(cx, cy, right, top)) return { inside: false, handle: 'ne' };
    if (within(cx, cy, right, bottom)) return { inside: false, handle: 'se' };
    if (within(cx, cy, left, bottom)) return { inside: false, handle: 'sw' };

    if (within(cx, cy, midX, top)) return { inside: false, handle: 'n' };
    if (within(cx, cy, right, midY)) return { inside: false, handle: 'e' };
    if (within(cx, cy, midX, bottom)) return { inside: false, handle: 's' };
    if (within(cx, cy, left, midY)) return { inside: false, handle: 'w' };

    const inside = cx >= left && cx <= right && cy >= top && cy <= bottom;
    return { inside, handle: null };
  };

  const containerDeltaToMapDelta = (dxPx, dyPx, anchorLatLng) => {
    const startLL = anchorLatLng;
    const startCP = map.latLngToContainerPoint(startLL);
    const curCP = L.point(startCP.x + dxPx, startCP.y + dyPx);
    const curLL = map.containerPointToLatLng(curCP);
    return {
      dx: curLL.lng - startLL.lng,
      dy: curLL.lat - startLL.lat,
    };
  };

  const onMouseDown = (e) => {
    if (!bounds || locked || !editing) return;
    e.preventDefault();
    e.stopPropagation();

    const cont = map.getContainer().getBoundingClientRect();
    const pt = { x: e.clientX - cont.left, y: e.clientY - cont.top };
    const box = boundsToContainerBox(bounds);
    const hit = hitTest(pt, box);

    startRef.current = {
      containerPt: pt,
      bounds: JSON.parse(JSON.stringify(bounds)),
    };

    if (hit.handle) {
      setResizing(hit.handle);
      setCursor(
        hit.handle === 'n' || hit.handle === 's'
          ? 'ns-resize'
          : hit.handle === 'e' || hit.handle === 'w'
          ? 'ew-resize'
          : hit.handle === 'ne' || hit.handle === 'sw'
          ? 'nesw-resize'
          : 'nwse-resize'
      );
    } else if (hit.inside) {
      setDragging(true);
      setCursor('move');
    }
  };

  const onMouseMove = (e) => {
    if (!bounds || locked || !editing) return;
    if (!dragging && !resizing) return;
    e.preventDefault();

    const cont = map.getContainer().getBoundingClientRect();
    const cur = { x: e.clientX - cont.left, y: e.clientY - cont.top };
    const start = startRef.current.containerPt;
    const startBounds = startRef.current.bounds;

    const dPxX = cur.x - start.x;
    const dPxY = cur.y - start.y;

    const [[sy0, sx0], [sy1, sx1]] = startBounds;
    const centerLat = (sy0 + sy1) / 2;
    const centerLng = (sx0 + sx1) / 2;

    const { dx, dy } = containerDeltaToMapDelta(
      dPxX,
      dPxY,
      L.latLng(centerLat, centerLng)
    );

    if (dragging) {
      setBounds((prev) => {
        if (!prev) return prev;
        const [[y0, x0], [y1, x1]] = startBounds;
        const ny0 = y0 + dy;
        const ny1 = y1 + dy;
        const nx0 = x0 + dx;
        const nx1 = x1 + dx;
        return [
          [ny0, nx0],
          [ny1, nx1],
        ];
      });
      return;
    }

    if (resizing) {
      setBounds((prev) => {
        if (!prev) return prev;

        const startBox = boundsToContainerBox(startBounds);
        if (!startBox) return prev;

        const { left, right, top, bottom } = startBox;

        let newLeftX = left;
        let newRightX = right;
        let newTopY = top;
        let newBottomY = bottom;

        if (resizing.includes('w')) newLeftX = cur.x;
        if (resizing.includes('e')) newRightX = cur.x;
        if (resizing.includes('n')) newTopY = cur.y;
        if (resizing.includes('s')) newBottomY = cur.y;

        if (newRightX - newLeftX < MIN_SIZE_PX) {
          if (resizing.includes('w')) newLeftX = newRightX - MIN_SIZE_PX;
          else if (resizing.includes('e')) newRightX = newLeftX + MIN_SIZE_PX;
        }
        if (newBottomY - newTopY < MIN_SIZE_PX) {
          if (resizing.includes('n')) newTopY = newBottomY - MIN_SIZE_PX;
          else if (resizing.includes('s')) newBottomY = newTopY + MIN_SIZE_PX;
        }

        const midX = (left + right) / 2;
        const midY = (top + bottom) / 2;

        const topLL = map.containerPointToLatLng(L.point(midX, newTopY));
        const bottomLL = map.containerPointToLatLng(
          L.point(midX, newBottomY)
        );
        const leftLL = map.containerPointToLatLng(L.point(newLeftX, midY));
        const rightLL = map.containerPointToLatLng(L.point(newRightX, midY));

        const topLat = topLL.lat;
        const bottomLat = bottomLL.lat;
        const leftLng = leftLL.lng;
        const rightLng = rightLL.lng;

        const cp0 = map.latLngToContainerPoint(L.latLng(sy0, sx0));
        const cp1 = map.latLngToContainerPoint(L.latLng(sy1, sx1));
        const isY0Top = cp0.y < cp1.y;
        const isX0Left = cp0.x < cp1.x;

        const ny0 = isY0Top ? topLat : bottomLat;
        const ny1 = isY0Top ? bottomLat : topLat;
        const nx0 = isX0Left ? leftLng : rightLng;
        const nx1 = isX0Left ? rightLng : leftLng;

        return [
          [ny0, nx0],
          [ny1, nx1],
        ];
      });
    }
  };

  const onMouseUp = (e) => {
    if (!dragging && !resizing) return;
    e.preventDefault();
    setDragging(false);
    setResizing(null);
    setCursor('default');
  };

  useEffect(() => {
    if (!editing) return undefined;
    if (!dragging && !resizing) return undefined;

    const mm = (ev) => onMouseMove(ev);
    const mu = (ev) => onMouseUp(ev);
    window.addEventListener('mousemove', mm, { passive: false });
    window.addEventListener('mouseup', mu, { passive: false });
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);
    };
  }, [editing, dragging, resizing, bounds, locked]);

  const box = editing && bounds ? boundsToContainerBox(bounds) : null;

  const stopAll = (e) => {
    e.stopPropagation();
  };

  const notifyUiActive = (active) => {
    if (typeof onUiActiveChange === 'function') {
      onUiActiveChange(active);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1550,
        pointerEvents: 'none',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      {!hideMenu && overlayChoices.length > 0 && (
        <div
          data-pa-floors-panel="1"
          style={{
            position: 'absolute',
            left: 14,
            bottom: 14,
            maxWidth: sizePreset.maxWidth,
            padding: sizePreset.padding,
            borderRadius: 10,
            background: 'rgba(15,18,30,0.96)',
            border: '1px solid rgba(80,90,130,0.9)',
            boxShadow: '0 8px 18px rgba(0,0,0,0.7)',
            fontSize: sizePreset.fontSize,
            color: '#f4f5ff',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            pointerEvents: 'auto',
          }}
          onMouseDown={stopAll}
          onClick={stopAll}
          onDoubleClick={stopAll}
          onMouseEnter={() => notifyUiActive(true)}
          onMouseLeave={() => notifyUiActive(false)}
        >
          <div
            style={{
              fontWeight: 500,
              opacity: 0.9,
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: 999,
                background: 'rgba(120,180,255,0.9)',
              }}
            />
            <span>{resolvedMenuTitle}</span>
          </div>
          <select
            value={selectedOverlayId}
            onChange={handleOverlayChange}
            style={{
              width: '100%',
              padding: sizePreset.selectPadding,
              borderRadius: 6,
              border: '1px solid rgba(90,100,150,0.9)',
              background: '#111320',
              color: '#f4f5ff',
              fontSize: sizePreset.fontSize,
              outline: 'none',
            }}
            onMouseDown={stopAll}
            onClick={stopAll}
            onDoubleClick={stopAll}
          >
            {overlayChoices
              .slice()
              .reverse()
              .map((a) => {
                const label = a.title || a.name || 'Untitled overlay';
                return (
                  <option key={a.id} value={String(a.id)}>
                    {label}
                  </option>
                );
              })}
            <option value="none">{resolvedBaseLabel}</option>
          </select>
        </div>
      )}

      {editing && box && (
        <div
          onMouseDown={onMouseDown}
          style={{
            position: 'absolute',
            inset: 0,
            cursor,
            pointerEvents: 'auto',
            touchAction: 'none',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: box.left,
              top: Math.max(10, box.top - 56),
              padding: '6px 10px',
              borderRadius: 6,
              background: 'rgba(20,20,30,0.96)',
              color: 'rgba(245,245,255,0.98)',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 10px rgba(0,0,0,0.6)',
              pointerEvents: 'auto',
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Overlay opacity</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={opacity}
              onChange={handleOpacityChange}
              style={{ flex: 1 }}
            />
            <span style={{ minWidth: 36, textAlign: 'right' }}>
              {Math.round(opacity * 100)}%
            </span>
          </div>

          <div
            style={{
              position: 'absolute',
              left: box.left,
              top: box.top,
              width: box.width,
              height: box.height,
              border: '2px dashed rgba(255,255,255,0.9)',
              boxShadow: '0 0 0 99999px rgba(0,0,0,0.15) inset',
              borderRadius: 4,
              pointerEvents: 'none',
            }}
          />

          {['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map((pos) => {
            const handlePos = (() => {
              const { left, top, right, bottom } = box;
              const midX = (left + right) / 2;
              const midY = (top + bottom) / 2;
              switch (pos) {
                case 'nw':
                  return { x: left, y: top, cursor: 'nwse-resize' };
                case 'n':
                  return { x: midX, y: top, cursor: 'ns-resize' };
                case 'ne':
                  return { x: right, y: top, cursor: 'nesw-resize' };
                case 'e':
                  return { x: right, y: midY, cursor: 'ew-resize' };
                case 'se':
                  return { x: right, y: bottom, cursor: 'nwse-resize' };
                case 's':
                  return { x: midX, y: bottom, cursor: 'ns-resize' };
                case 'sw':
                  return { x: left, y: bottom, cursor: 'nesw-resize' };
                case 'w':
                  return { x: left, y: midY, cursor: 'ew-resize' };
                default:
                  return { x: left, y: top, cursor: 'default' };
              }
            })();
            const LOCAL_HANDLE_SIZE = 10;
            return (
              <div
                key={pos}
                style={{
                  position: 'absolute',
                  left: handlePos.x - LOCAL_HANDLE_SIZE,
                  top: handlePos.y - LOCAL_HANDLE_SIZE,
                  width: LOCAL_HANDLE_SIZE * 2,
                  height: LOCAL_HANDLE_SIZE * 2,
                  borderRadius: 4,
                  background: 'rgba(60,109,246,0.95)',
                  border: '1px solid rgba(255,255,255,0.9)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
                  pointerEvents: 'auto',
                  cursor: handlePos.cursor,
                }}
              />
            );
          })}

          {locked && (
            <div
              style={{
                position: 'absolute',
                left: box.left,
                top: box.top - 28,
                padding: '4px 8px',
                borderRadius: 6,
                background: '#2b2e3e',
                color: '#e7e8ff',
                fontSize: 12,
                border: '1px solid #3a3d52',
                pointerEvents: 'auto',
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              Overlay is locked (unlock in the Edit tab)
            </div>
          )}
        </div>
      )}
    </div>
  );
}
